import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Product } from '../types/common';
import axios from 'axios';
import { RootState } from '../store/store';

export interface ExportProduct {
    Артикул: string;
    Наименование: string;
    Цвет: string;
    Категория: string;
    Количество: number;
    Вес: number;
    Цена: number;
    Скидка: string | number;
}

export async function exportProductsToExcel(products: Product[], fileName = 'Список_товаров.xlsx'): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet('Товары');

    // ---------- хелпер для извлечения значений колонки "Размер" ----------
    function pickSizeFromGroup(p?: Product | null): string {
        if (!p || typeof p !== 'object') return '';
        const g: any = (p as any).group;
        const url = typeof g?.url === 'string' ? g.url.trim() : '';
        const val = typeof g?.optionValue === 'string' ? g.optionValue.trim() : '';
        if (url === 'Размер' && val) return val;
        return '';
    }

    /* ---------- 1. Пустая строка ---------- */
    ws.addRow([]);

    /* ---------- 2. Заголовок ---------- */
    const titleRow = ws.addRow(['Список товаров']);
    titleRow.font = { bold: true, size: 14 };
    titleRow.alignment = { horizontal: 'center' };
    ws.mergeCells('A2:I2');

    /* ---------- 3. Пустая строка ---------- */
    ws.addRow([]);

    /* ---------- 4. Заголовки колонок ---------- */
    ws.columns = [
        { key: 'Артикул', width: 15 },
        { key: 'Наименование', width: 35 },
        { key: 'Размер', width: 18 },
        { key: 'Цвет', width: 25 },
        { key: 'Категория', width: 15 },
        { key: 'Количество', width: 15 },
        { key: 'Масса, кг', width: 15 },
        { key: 'Цена, ₽', width: 15 },
        { key: 'Скидка, %', width: 15 },
    ];

    const headerTitles = ['Артикул', 'Наименование', 'Размер', 'Цвет', 'Категория', 'Количество', 'Масса, кг', 'Цена, ₽', 'Скидка, %'];
    const headerRow = ws.addRow(headerTitles);
    headerRow.font = { bold: true };

    // числовые колонки (E-H) по центру
    ['E', 'F', 'G', 'H', 'I'].forEach((col) => (ws.getColumn(col).alignment = { horizontal: 'center' }));

    /* ---------- Данные ---------- */
    products.forEach((p) => {
        const size = pickSizeFromGroup(p);
        const color = (p as any).productСolor ?? (p as any).productColor ?? (p as any).color ?? '';
        ws.addRow([
            p.articleNumber ?? '',
            p.name ?? '',
            size,
            color,
            p.category?.name ?? '',
            p.quantity ?? 0,
            p.weight ?? 0,
            p.price ?? 0,
            (p as any).discount?.percent ?? '',
        ]);
    });

    /* ---------- Автофильтр ---------- */
    const lastDataRow = 4 + products.length; // 4 = заголовки
    ws.autoFilter = { from: 'A4', to: `I${lastDataRow}` };

    /* ---------- Пустая строка после таблицы ---------- */
    ws.addRow([]);

    /* ---------- Строка даты ---------- */
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    const dateRow = ws.addRow([`Дата: ${dd}.${mm}.${yyyy}`]);
    const dateRowNum = dateRow.number;
    ws.mergeCells(`A${dateRowNum}:I${dateRowNum}`);
    dateRow.alignment = { horizontal: 'right' };

    /* ---------- Заморозка верхних строк ---------- */
    ws.views = [{ state: 'frozen', ySplit: 4 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, fileName);
}

/* -----------------------------------------------------------------
   ЭКСПОРТ ОПЛАЧЕННЫХ ЗАКАЗОВ
------------------------------------------------------------------ */
type OrderForExport = RootState['common']['orders'][0];
interface ArticleDTO {
    _id: string;
    articleNumber: string;
}
interface EmailDTO {
    _id: string;
    email: string;
}
interface PromoDTO {
    code: string;
    discountType: 'percentage' | 'points';
    discountValue: string;
}
export async function exportOrdersToExcel(orders: OrderForExport[], fileName = 'Оплаченные_заказы.xlsx'): Promise<void> {
    /* -------- 1. Собираем уникальные ID для батч-запросов -------- */
    const productIds = new Set<string>();
    const userIds = new Set<string>();
    const promoCodes = new Set<string>();
    orders.forEach((o) => {
        o.cart.forEach((i) => {
            if (!(i as any).articleNumber) productIds.add(i.productId);
        });
        const uId = (o as any).userID;
        if (uId && !(o.user as any)?.email) userIds.add(uId);
        if (o.promoCode) {
            const codeKey = typeof o.promoCode === 'string' ? o.promoCode : (o.promoCode as any).code;
            promoCodes.add(codeKey);
        }
    });
    /* -------- 2. Подтягиваем из Mongo одним запросом -------- */
    const [articlesMap, emailsMap, promocodesMap] = await Promise.all([
        fetchArticleNumbers(Array.from(productIds)),
        fetchUserEmails(Array.from(userIds)),
        fetchPromoCodes(Array.from(promoCodes)),
    ]);
    /* -------- 3. Создаём workbook -------- */
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet('Заказы');
    ws.addRow([]);
    const titleRow = ws.addRow(['Оплаченные заказы']);
    titleRow.font = { bold: true, size: 14 };
    titleRow.alignment = { horizontal: 'center' };
    ws.mergeCells('A2:Q2');
    ws.addRow([]);
    ws.columns = [
        { key: 'dateTime', width: 16 },
        { key: 'orderNumber', width: 16 },
        { key: 'productNames', width: 70 },
        { key: 'articles', width: 18 },
        { key: 'totalWeight', width: 11 },
        { key: 'unitPrice', width: 20 },
        { key: 'discount', width: 20 },
        { key: 'finalUnitPrice', width: 20 },
        { key: 'quantity', width: 11 },
        { key: 'stockQty', width: 11 },
        { key: 'orderFullPrice', width: 20 },
        { key: 'orderTotal', width: 20 },
        { key: 'customerName', width: 25 },
        { key: 'phone', width: 15 },
        { key: 'address', width: 35 },
        { key: 'email', width: 25 },
        { key: 'comment', width: 50 },
    ];
    ws.addRow([
        'Дата и время',
        'Номер заказа',
        'Наименование товара',
        'Артикул товара',
        'Общая масса, кг',
        'Цена за единицу, ₽',
        'Скидка',
        'Итоговая цена, ₽',
        'Количество, шт',
        'Доступно со склада',
        'Итоговая цена заказа, ₽',
        'Сумма заказа, ₽',
        'ФИО покупателя',
        'Телефон',
        'Адрес доставки',
        'E-mail',
        'Комментарий',
    ]).font = { bold: true };
    /* Выравнивание / перенос */
    ['B', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'].forEach((c) => (ws.getColumn(c).alignment = { ...(ws.getColumn(c).alignment ?? {}), horizontal: 'center' }));
    ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q'].forEach((c) => {
        const cur = ws.getColumn(c).alignment ?? {};
        ws.getColumn(c).alignment = { ...cur, vertical: 'middle', wrapText: true };
    });
    /* -------- 4. Заполняем строки -------- */
    orders.forEach((o) => {
        const dt = new Date(o.timestamp);
        const formattedDT = `${dt.toLocaleDateString('ru-RU')} ${dt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;
        const names = o.cart.map((i) => i.name ?? '').join('\n');
        const articles = o.cart.map((i) => (i as any).articleNumber || articlesMap.get(i.productId) || '').join('\n');
        const unitPrices = o.cart.map((i) => i.price?.toFixed(2) ?? '0').join('\n');
        const codeKey = o.promoCode ? (typeof o.promoCode === 'string' ? o.promoCode : (o.promoCode as any).code) : null;
        const promo = codeKey ? promocodesMap.get(codeKey) : undefined;
        const discounts = o.cart
            .map((i) => {
                if (i.specialDiscountPercent) return `специальная - ${i.specialDiscountPercent}%`;
                if (o.birthdayDiscount) return 'ДР-скидка';
                if (promo && promo.discountType === 'percentage') return `${promo.code} - ${promo.discountValue}%`;
                if (codeKey) return `промокод - ${codeKey}`;
                if (o.paidWithPoints) return `баллы - ${o.paidWithPoints}`;
                return '—';
            })
            .join('\n');

        const finalPrices = o.cart
            .map((i) => {
                if (i.specialDiscountedPrice !== undefined) {
                    return i.specialDiscountedPrice.toFixed(2);
                }
                if (promo && promo.discountType === 'percentage') {
                    const dv = Number(promo.discountValue) / 100;
                    const priceAfterPromo = (i.price ?? 0) * (1 - dv);
                    return priceAfterPromo.toFixed(2);
                }
                return i.price?.toFixed(2) ?? '0';
            })
            .join('\n');
        const qty = o.cart.map((i) => i.quantity ?? 0).join('\n');
        const stockQtys = o.cart.map((i) => ((i as any).stockQuantity !== undefined ? (i as any).stockQuantity : '')).join('\n');
        const email = (o.user as any)?.email || emailsMap.get((o as any).userID ?? '') || '';
        const fullOrderPrice = o.cart.reduce((sum, i) => sum + (i.price ?? 0) * (i.quantity ?? 0), 0).toFixed(2);
        ws.addRow([
            formattedDT,
            o.orderNumber ?? '',
            names,
            articles,
            o.totalWeight?.toFixed(2) ?? 0,
            unitPrices,
            discounts,
            finalPrices,
            qty,
            stockQtys,
            fullOrderPrice,
            o.totalPrice?.toFixed(2) ?? 0,
            o.user?.name ?? '',
            o.user?.phone ?? '',
            o.user?.address?.raw ?? '',
            email,
            o.orderComment ?? '',
        ]);
    });
    const lastDataRow = 4 + orders.length;
    ws.autoFilter = { from: 'A4', to: `Q${lastDataRow}` };
    ws.addRow([]);
    const now = new Date();
    ws.addRow([`Дата выгрузки: ${now.toLocaleDateString('ru-RU')}`]);
    ws.mergeCells(`A${ws.lastRow!.number}:Q${ws.lastRow!.number}`);
    ws.getRow(ws.lastRow!.number).alignment = { horizontal: 'left' };
    ws.views = [{ state: 'frozen', ySplit: 4 }];
    const blob = new Blob([await workbook.xlsx.writeBuffer()], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, fileName);
}

/* -----------------------------------------------------------------
   Вспомогательные функции для получения артикулов и e-mails
------------------------------------------------------------------ */
let cachedCsrfToken: string | null = null;
async function getCsrfToken(): Promise<string> {
    if (cachedCsrfToken) return cachedCsrfToken;
    const { data } = await axios.get<{ csrfToken: string }>(`${process.env.REACT_APP_SERVER_URL}/csrf-token`, { withCredentials: true });
    cachedCsrfToken = data.csrfToken;
    return cachedCsrfToken;
}
async function fetchArticleNumbers(ids: string[]): Promise<Map<string, string>> {
    if (!ids.length) return new Map();
    const csrfToken = await getCsrfToken();
    const { data } = await axios.post<ArticleDTO[]>(
        `${process.env.REACT_APP_SERVER_URL}/products/articles`,
        { ids },
        { headers: { 'csrf-token': csrfToken }, withCredentials: true }
    );
    return new Map(data.map(({ _id, articleNumber }) => [_id, articleNumber]));
}
async function fetchUserEmails(ids: string[]): Promise<Map<string, string>> {
    if (!ids.length) return new Map();
    const csrfToken = await getCsrfToken();
    const { data } = await axios.post<EmailDTO[]>(
        `${process.env.REACT_APP_SERVER_URL}/user/emails`,
        { ids },
        { headers: { 'csrf-token': csrfToken }, withCredentials: true }
    );
    return new Map(data.map(({ _id, email }) => [_id, email]));
}
async function fetchPromoCodes(codes: string[]): Promise<Map<string, PromoDTO>> {
    if (!codes.length) return new Map();
    const csrfToken = await getCsrfToken();
    const { data } = await axios.get<PromoDTO[]>(`${process.env.REACT_APP_SERVER_URL}/promocodes`, { headers: { 'csrf-token': csrfToken }, withCredentials: true });
    const filtered = data.filter((p) => codes.includes(p.code));
    return new Map(filtered.map((p) => [p.code, p]));
}
